import React, { FC, useEffect, useMemo, useState } from "react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from "chart.js"
import { Line } from "react-chartjs-2"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { mapToCamelCase, addCommasToNumber, yyyymmToDate, replaceCurrencies } from "../../../../../utility"
import { AdvanceDealDetail, AdvanceDealRow, DealDetailMonth, UserAdvanceProgressValues } from "../../../../../types"
import Loading from "../../../../ui/Loading"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend)

const PURPLE = "#7c3aed"
const GREEN = "#22c55e"
const AMBER = "#f59e0b"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const CHART_FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"

type AdvanceDealDetailPageProps = {
  selectedDeal: AdvanceDealRow
  onBack: () => void
}

// ---- formatting -------------------------------------------------------------

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${addCommasToNumber(Math.round(value))}`

const compactUsd = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${Math.round(value)}`
}

const pct = (value: number | null | undefined, digits = 0) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${parseFloat(value.toFixed(digits))}%`

const multipleX = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(2)}x`

const xirrPct = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || isNaN(fraction) ? "N/A" : `${(fraction * 100).toFixed(1)}%`

const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  const year = key.slice(0, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (!year || isNaN(month)) return "N/A"
  return new Date(parseInt(year, 10), month - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

const monthYearShort = (key: string) => {
  const year = key.slice(2, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (isNaN(month)) return key
  return `${new Date(2000, month - 1, 1).toLocaleDateString("en-US", { month: "short" })} ${year}`
}

// ---- small components -------------------------------------------------------

const Badge: FC<{ text: string; bg: string; color: string }> = ({ text, bg, color }) => (
  <span
    style={{
      backgroundColor: bg,
      color,
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap"
    }}
  >
    {text}
  </span>
)

const KpiCard: FC<{ label: string; value: string; subtext?: string; subtextColor?: string }> = ({
  label,
  value,
  subtext,
  subtextColor = TEXT_MUTED
}) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <KTitleSpan text={value} fontSize={22} fontWeight={700} color={TEXT_PRIMARY} />
    {subtext ? <KSpan text={subtext} fontSize={12} color={subtextColor} /> : null}
  </div>
)

const ProgressBar: FC<{ label: string; pctValue: number; color: string }> = ({ label, pctValue, color }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex justify-between items-center">
      <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
      <KSpan text={`${Math.round(pctValue)}%`} fontSize={13} fontWeight={700} color={TEXT_PRIMARY} />
    </div>
    <div style={{ height: 10, background: "#f1f5f9", borderRadius: 999 }}>
      <div style={{ width: `${Math.min(100, pctValue)}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  </div>
)

const Milestone: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 items-center text-center">
    <KSpan text={label} fontSize={10} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.04em" />
    <KSpan text={value} fontSize={12} fontWeight={600} color={TEXT_PRIMARY} />
  </div>
)

const thStyle: React.CSSProperties = {
  textAlign: "right",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: TEXT_MUTED,
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e5e7eb"
}

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap",
  textAlign: "right"
}

// ---- monthly performance chart ----------------------------------------------

// Inline plugin: shades the "kahuna revenue phase" (the window between company
// recoup and artist recoup) and draws dashed recoup marker lines. Registered
// per-chart via the `plugins` prop so it never leaks into the other charts that
// share the global ChartJS registry.
const recoupPhasePlugin = {
  id: "recoupPhase",
  beforeDatasetsDraw(chart: any) {
    const opts = chart.options.plugins?.recoupPhase
    if (!opts) return
    const { ctx, chartArea, scales } = chart
    const { companyIdx, artistIdx } = opts
    if (companyIdx === null || artistIdx === null || companyIdx === undefined || artistIdx === undefined) return
    const x1 = scales.x.getPixelForValue(Math.min(companyIdx + 1, artistIdx + 1))
    const x2 = chartArea.right

    ctx.save()
    ctx.fillStyle = "rgba(34,197,94,0.09)"
    ctx.fillRect(x1, chartArea.top, x2 - x1, chartArea.bottom - chartArea.top)
    if (x2 - x1 > 28) {
      ctx.translate((x1 + x2) / 2, (chartArea.top + chartArea.bottom) / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.fillStyle = "rgba(21,128,61,0.55)"
      ctx.font = `700 10px ${CHART_FONT}`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText("KAHUNA REVENUE PHASE", 0, 0)
    }
    ctx.restore()
  },
  afterDatasetsDraw(chart: any) {
    const opts = chart.options.plugins?.recoupPhase
    if (!opts) return
    const { ctx, chartArea, scales } = chart
    const drawMarker = (idx: number | null, color: string, label: string) => {
      if (idx === null || idx === undefined) return
      const x = scales.x.getPixelForValue(idx)
      ctx.save()
      ctx.setLineDash([10, 15])
      ctx.lineWidth = 1.5
      ctx.strokeStyle = color
      ctx.beginPath()
      ctx.moveTo(x, chartArea.top)
      ctx.lineTo(x, chartArea.bottom)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.translate(x + 5, chartArea.bottom - 6)
      ctx.rotate(-Math.PI / 2)
      ctx.fillStyle = color
      ctx.font = `600 9px ${CHART_FONT}`
      ctx.textAlign = "left"
      ctx.textBaseline = "middle"
      ctx.fillText(label, 0, 0)
      ctx.restore()
    }
    drawMarker(opts.companyIdx + 1, "#22c55e70", "Company Recoup")
    drawMarker(opts.artistIdx + 1, "#11111152", "Artist Recoup Est.")
  }
}

const MonthlyPerformanceChart: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const months = [...Object.keys(selectedDeal.royalties), ...Object.keys(selectedDeal.projections)]
  const labels = months.map((m) => monthYearShort(m))
  const reportedThrough = Object.keys(selectedDeal.royalties).reduce((a, b) => (a > b ? a : b))
  const projectedThrough = Object.keys(selectedDeal.projections).reduce((a, b) => (a > b ? a : b))

  const findIdx = (key: string | null) => {
    if (!key) return null
    const i = months.findIndex((m) => m === key)
    return i >= 0 ? i : null
  }

  const companyIdx = findIdx(selectedDeal.companyRecoupDate)
  const artistIdx = findIdx(selectedDeal.artistRecoupDate)

  // Dash the projected tail of each line.
  const dashSegment = (ctx: any) => {
    return reportedThrough < months[ctx.p1DataIndex] ? [5, 4] : undefined
  }

  const cashflowMap: Record<string, number> = {}

  // 1. Add royalties (positive)
  let firstMonth = ""
  Object.entries(selectedDeal.royalties ?? {}).forEach(([month, value]) => {
    if (!firstMonth || month < firstMonth) {
      firstMonth = month
    }
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 2. Add projections (positive)
  Object.entries(selectedDeal.projections ?? {}).forEach(([month, value]) => {
    cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
  })

  // 3. Add advances (negative)
  Object.entries(selectedDeal.advances ?? {}).forEach(([month, value]) => {
    const monthToUse = month < firstMonth ? firstMonth : month
    cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
  })

  const cashflow: Record<string, number> = {}
  let currentCash = 0

  Object.keys(cashflowMap)
    .sort()
    .forEach((month) => {
      currentCash += cashflowMap[month]
      if (currentCash > 0) currentCash = 0
      cashflow[month] = currentCash
    })

  months.unshift("000000")
  labels.unshift("Adv")
  cashflow["000000"] = selectedDeal.advance * -1
  selectedDeal.artistBalance.royalties["000000"] = selectedDeal.advance * -1

  const data = {
    labels,
    datasets: [
      {
        label: "GROSS ROYALTIES",
        data: months.map((m) => {
          if (selectedDeal.royalties[m] !== undefined) return selectedDeal.royalties[m]
          else return selectedDeal.projections[m]
        }),
        borderColor: AMBER,
        backgroundColor: "rgba(245,158,11,0.08)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y1",
        segment: { borderDash: dashSegment }
      },
      {
        label: "COMPANY OUT. BALANCE",
        data: months.map((m) => cashflow[m]),
        borderColor: GREEN,
        backgroundColor: "rgba(124,58,237,0.05)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      },
      {
        label: "ARTIST OUT. BALANCE",
        data: months.map((m) => {
          if (selectedDeal.artistBalance.royalties[m] !== undefined) return selectedDeal.artistBalance.royalties[m]
          else return selectedDeal.artistBalance.projections[m]
        }),
        borderColor: "#11111192",
        backgroundColor: "rgba(34,197,94,0.05)",
        tension: 0.3,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: "y",
        segment: { borderDash: dashSegment }
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "center",
        labels: {
          boxWidth: 22,
          boxHeight: 2,
          usePointStyle: false,
          font: { family: CHART_FONT, size: 11, weight: "600" },
          color: TEXT_MUTED
        }
      },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${compactUsd(ctx.parsed.y)}` } },
      // chartjs-plugin-datalabels is registered globally by other charts; a
      // per-point label on a 48-month, 3-line trend chart is unreadable, so we
      // turn it off here and surface exact values via the hover tooltip.
      datalabels: { display: false },
      recoupPhase: { companyIdx, artistIdx }
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxTicksLimit: 10, font: { family: CHART_FONT, size: 10 } } },
      y: {
        position: "right",
        title: {
          display: true,
          text: "Outstanding Balance",
          font: { family: CHART_FONT, size: 10 },
          color: TEXT_MUTED
        },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" }
      },
      y1: {
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Gross Royalties", font: { family: CHART_FONT, size: 10 }, color: TEXT_MUTED },
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { drawOnChartArea: false }
      }
    }
  }
  return (
    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text="Monthly Performance" fontSize={16} fontWeight={700} />
      <KSpan
        text={`Reported through ${monthYear(reportedThrough)} · Projected through ${monthYear(projectedThrough)}`}
        fontSize={12}
        color={TEXT_MUTED}
      />
      <div style={{ height: 360, marginTop: 16 }}>
        <Line data={data} options={options} plugins={[recoupPhasePlugin]} />
      </div>
    </div>
  )
}

// ---- monthly breakdown table ------------------------------------------------

const MonthlyBreakdownTable: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  const [showAll, setShowAll] = useState(false)

  const projectionMonths = Object.keys(selectedDeal.projections)
  const royaltiesMonths = Object.keys(selectedDeal.royalties)

  const months = [...royaltiesMonths, ...projectionMonths]

  const sortedAllMonths = [...months].sort((a, b) => (a < b ? 1 : -1))
  const sortedRoyaltiesMonths = [...royaltiesMonths].sort((a, b) => (a < b ? 1 : -1))

  const visibleMonths = showAll ? sortedAllMonths : sortedRoyaltiesMonths

  const renderAggragateRow = (userAdvanceProgressValues: UserAdvanceProgressValues, month: string, color = false) => {
    let value = undefined
    let valutStr = "-"
    if (userAdvanceProgressValues.royalties[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(
        userAdvanceProgressValues.royalties[month].toFixed(2)
      )}`
      value = userAdvanceProgressValues.royalties[month]
    } else if (userAdvanceProgressValues.projections[month] !== undefined) {
      valutStr = `${replaceCurrencies("USD")} ${addCommasToNumber(
        userAdvanceProgressValues.projections[month].toFixed(2)
      )}`
      value = userAdvanceProgressValues.projections[month]
    }

    let colorStyle = {}
    if (color && value !== undefined) colorStyle = { color: value >= 0 ? "green" : "red" }

    return <td style={{ ...tdStyle, ...colorStyle }}>{valutStr}</td>
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    // 1. Add royalties (positive)
    const cashflowMap: Record<string, number> = {}

    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) {
        firstMonth = month
      }
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 2. Add projections (positive)
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })

    // 3. Add advances (negative)
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })

    // 4. Convert to sorted array format
    const cashflow: Record<string, number> = {}
    let currentCash = 0

    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })

    return { royalties: cashflow, advances: {}, projections: {} }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <KTitleSpan text="Monthly Breakdown" fontSize={16} fontWeight={700} />
      <div style={{ overflowX: "auto", marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>Month</th>
              <th style={thStyle}>Gross Collections</th>
              <th style={thStyle}>Kahuna Revenue</th>
              <th style={thStyle}>Advance Recoup Applied</th>
              <th style={thStyle}>Artist Payable</th>
              <th style={thStyle}>New Advance Funded</th>
              <th style={thStyle}>Outstanding Advance Balance (EoM)</th>
              <th style={thStyle}>Kahuna Net Cash Flow for IRR</th>
              <th style={thStyle}>Kahuna Balance</th>
            </tr>
          </thead>
          <tbody>
            {visibleMonths.map((month) => {
              const advanceTimelineMap = { royalties: selectedDeal.advancesTimeline, projections: {}, advances: {} }
              const kahunaBalanceMap = {
                royalties: selectedDeal.royalties,
                projections: selectedDeal.projections,
                advances: selectedDeal.advances
              }
              return (
                <tr key={month} style={{ opacity: projectionMonths.includes(month) ? 0.6 : 1 }}>
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    {monthYear(month)}
                    {projectionMonths.includes(month) && <KSpan color={TEXT_MUTED} fontSize={11} text=" · proj" />}
                  </td>
                  {renderAggragateRow(selectedDeal.actualGrossAndProjection, month)}
                  {renderAggragateRow(selectedDeal.kahunaRevenue, month)}
                  {renderAggragateRow(selectedDeal.kahunaAdvanceCollection, month)}
                  {renderAggragateRow(selectedDeal.artistPayable, month)}
                  {renderAggragateRow(advanceTimelineMap, month)}
                  {renderAggragateRow(selectedDeal.artistBalance, month, true)}
                  {renderAggragateRow(selectedDeal.kahunaTotal, month)}
                  {renderAggragateRow(convertToCumulativeMap(kahunaBalanceMap), month, true)}
                </tr>
              )
            })}
            {visibleMonths.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={8}>
                  No monthly data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {sortedAllMonths.length > sortedRoyaltiesMonths.length && (
        <div className="flex justify-center mt-3">
          <KButton
            text={showAll ? "Hide projections" : `Show projections`}
            background="transparent"
            shadowDisabled
            onClick={() => setShowAll(!showAll)}
          />
        </div>
      )}
    </div>
  )
}

// ---- tranche history --------------------------------------------------------

const postRecoupDurationText = (value: number | null) => {
  if (value === null || value === undefined) return "N/A"
  // Backend stores the duration as a plain number of months; append the unit.
  return /^\d+$/.test(String(value).trim()) ? `${value * 12} Months` : String(value)
}

const TrancheHistory: FC<{ selectedDeal: AdvanceDealRow }> = ({ selectedDeal }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-4">
      <KTitleSpan text="Deal Offer" fontSize={16} fontWeight={700} lineHeight="20px" />
      <div className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3 bg-gray-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: PURPLE,
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {selectedDeal.advanceCount}
            </div>
            <div className="flex flex-col">
              <KSpan
                text={`Advance # ${selectedDeal.advanceCount}`}
                fontSize={14}
                fontWeight={700}
                color={TEXT_PRIMARY}
              />
              <KSpan
                text={`FUNDED ${yyyymmToDate(selectedDeal.dealDate)
                  .toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric"
                  })
                  .toUpperCase()}`}
                fontSize={11}
                fontWeight={500}
                color={TEXT_MUTED}
                letterSpacing="0.03em"
              />
            </div>
          </div>
          {selectedDeal.recouped ? (
            <Badge text="RECOUPED" bg="#dcfce7" color="#15803d" />
          ) : (
            <Badge text="OUTSTANDING" bg="#fef3c7" color="#b45309" />
          )}
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <KSpan text="Funding Terms" fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.04em" />
            <div className="mt-1.5 flex flex-col gap-1">
              <Row label="Advance Amount" value={usd0(selectedDeal.advance)} />
              <Row
                label="Recoup Buffer"
                value={`${((selectedDeal.recoupTarget - selectedDeal.advance) / selectedDeal.advance).toFixed(
                  2
                )}% (${usd0(selectedDeal.recoupTarget - selectedDeal.advance)})`}
              />
              <Row label="Recoup Target" value={usd0(selectedDeal.recoupTarget)} />
            </div>
          </div>
          <div>
            <KSpan text="Deal Terms" fontSize={11} fontWeight={700} color={TEXT_MUTED} letterSpacing="0.04em" />
            <div className="mt-1.5 flex flex-col gap-1">
              <Row label="Contract Model" value={selectedDeal.contractModel} />
              {selectedDeal.contractModel === "post_recoup" ? (
                <Row label="Post-Recoup Duration" value={postRecoupDurationText(selectedDeal.postRecoupDuration)} />
              ) : (
                <Row label="Fixed Term" value={`${(selectedDeal.contractDuration || 0) * 12} Months`} />
              )}
              <Row label="Current Commission" value={selectedDeal.commission?.toString()} />
              <div style={{ borderBottom: "1px dashed grey" }} />
              <Row
                label="Pre-Recoup Commission"
                value={selectedDeal.userExpense?.preRecoupCommission?.toString() || "-"}
              />
              <Row
                label="Post-Recoup Commission"
                value={selectedDeal.userExpense?.postRecoupCommission?.toString() || "-"}
              />
              <Row label="Distribution Fee" value={selectedDeal.userExpense?.distributionFee?.toString() || "-"} />
              <Row
                label="Post-Recoup Dist. Fee"
                value={selectedDeal.userExpense?.distributionFeePostRecoup?.toString() || "-"}
              />
              <div style={{ borderBottom: "1px dashed grey" }} />
              {!selectedDeal.dealOffer ? (
                <Row label="No Deal Attached" value="" />
              ) : (
                <>
                  <Row label="Exclusivity" value={selectedDeal.dealOffer?.contractExclusivity ? "Yes" : "No"} />
                  <Row
                    label="New Releases"
                    value={selectedDeal.dealOffer?.newReleaseIncludeRecoup ? "Included" : "Not included"}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3">
    <KSpan text={label} fontSize={12} color={TEXT_MUTED} />
    <KSpan text={value} fontSize={12} fontWeight={600} color={TEXT_PRIMARY} />
  </div>
)

// ---- CSV export -------------------------------------------------------------

const exportMonthlyCsv = (selectedDeal?: AdvanceDealRow) => {
  if (!selectedDeal) return

  const projectionMonths = Object.keys(selectedDeal.projections)
  const royaltiesMonths = Object.keys(selectedDeal.royalties)
  const allMonths = [...royaltiesMonths, ...projectionMonths].sort((a, b) => (a < b ? 1 : -1))

  const getVal = (userAdvanceProgressValues: UserAdvanceProgressValues, month: string): string => {
    let value: number | undefined
    if (userAdvanceProgressValues.royalties[month] !== undefined) {
      value = userAdvanceProgressValues.royalties[month]
    } else if (userAdvanceProgressValues.projections[month] !== undefined) {
      value = userAdvanceProgressValues.projections[month]
    }
    return value !== undefined ? value.toFixed(2) : ""
  }

  const convertToCumulativeMap = (userAdvanceProgressValues: UserAdvanceProgressValues) => {
    const cashflowMap: Record<string, number> = {}
    let firstMonth = ""
    Object.entries(userAdvanceProgressValues.royalties ?? {}).forEach(([month, value]) => {
      if (!firstMonth || month < firstMonth) firstMonth = month
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.projections ?? {}).forEach(([month, value]) => {
      cashflowMap[month] = (cashflowMap[month] || 0) + (value || 0)
    })
    Object.entries(userAdvanceProgressValues.advances ?? {}).forEach(([month, value]) => {
      const monthToUse = month < firstMonth ? firstMonth : month
      cashflowMap[monthToUse] = (cashflowMap[monthToUse] || 0) - (value || 0)
    })
    const cashflow: Record<string, number> = {}
    let currentCash = 0
    Object.keys(cashflowMap)
      .sort()
      .forEach((month) => {
        currentCash += cashflowMap[month]
        cashflow[month] = currentCash
      })
    return { royalties: cashflow, advances: {}, projections: {} }
  }

  const kahunaBalanceMap = {
    royalties: selectedDeal.royalties,
    projections: selectedDeal.projections,
    advances: selectedDeal.advances
  }
  const cumulativeMap = convertToCumulativeMap(kahunaBalanceMap)

  const header = [
    "Month",
    "Gross Collections",
    "Kahuna Revenue",
    "Advance Recoup Applied",
    "Artist Payable",
    "New Advance Funded",
    "Outstanding Advance Balance (EoM)",
    "Kahuna Net Cash Flow for IRR",
    "Kahuna Balance"
  ]

  const lines = allMonths.map((month) => {
    const advanceTimelineMap = { royalties: selectedDeal.advancesTimeline, projections: {}, advances: {} }
    const isProjection = projectionMonths.includes(month)
    return [
      `${monthYear(month)}${isProjection ? " · proj" : ""}`,
      getVal(selectedDeal.actualGrossAndProjection, month),
      getVal(selectedDeal.kahunaRevenue, month),
      getVal(selectedDeal.kahunaAdvanceCollection, month),
      getVal(selectedDeal.artistPayable, month),
      getVal(advanceTimelineMap, month),
      getVal(selectedDeal.artistBalance, month),
      getVal(selectedDeal.kahunaTotal, month),
      getVal(cumulativeMap, month)
    ].join(",")
  })

  const csv = [header.join(","), ...lines].join("\n")
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = `deal_${selectedDeal.artistName.replace(/\s+/g, "_")}_monthly.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ---- main -------------------------------------------------------------------

const AdvanceDealDetailPage: FC<AdvanceDealDetailPageProps> = ({ selectedDeal, onBack }) => {
  const [loading, setLoading] = useState(false)

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (!selectedDeal) {
    return (
      <div className="px-10 pt-5 pb-10 flex flex-col gap-4">
        <KButton width="100px" onClick={() => onBack()} text="← Back" background="transparent" shadowDisabled />
        <KSpan text="No deal data available for this artist." color="#666" />
      </div>
    )
  }

  return (
    <div className="px-10 pt-5 pb-10 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            className="text-xl text-gray-500 hover:text-gray-800"
            onClick={onBack}
            title="Back to Portfolio Overview"
          >
            ←
          </button>
          {selectedDeal.artistImage ? (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: `center / cover no-repeat url(${selectedDeal.artistImage})`
              }}
            />
          ) : null}
          <KTitleSpan text={selectedDeal.artistName} fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
          {selectedDeal.recouped ? (
            <Badge text="RECOUPED" bg="#dcfce7" color="#15803d" />
          ) : (
            <Badge text="PERFORMING" bg="#f9fcdc" color="#767676" />
          )}
          <Badge text="ADV" bg="#ede9fe" color="black" />
        </div>
        <div>
          <KButton text="Export" background="black" textColor="white" onClick={() => exportMonthlyCsv(selectedDeal)} />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard label="TOTAL DEPLOYED" value={usd0(selectedDeal.advance)} />
        <KpiCard
          label="OUTSTANDING"
          value={usd0(selectedDeal.outstanding)}
          subtext={`${pct(selectedDeal.outstandingPctOfDeployed)} of deployed`}
        />
        <KpiCard
          label="RECOUP TARGET"
          value={usd0(selectedDeal.recoupTarget)}
          subtext={`Recoup multiplier ${multipleX(selectedDeal.recoupMultiplier)}`}
        />
        <KpiCard label="DEAL DATE" value={selectedDeal.dealDate || "-"} />
        <KpiCard label="RECOUP LAST 30D" value={usd0(selectedDeal.recoupLast30D)} subtext="Company-side" />
        <KpiCard label="12M IRR" value={xirrPct(selectedDeal.irr12M)} subtext="Normalized 12M IRR" />
        <KpiCard label="24M IRR" value={xirrPct(selectedDeal.irr24M)} subtext="Normalized 24M IRR" />
        <KpiCard label="36M IRR" value={xirrPct(selectedDeal.irr36M)} subtext="Normalized 36M IRR" />
        <KpiCard label="XIRR" value={xirrPct(selectedDeal.xirr)} subtext="All cash flows" />
        <KpiCard label="ECONOMIC MOIC" value={multipleX(selectedDeal.economicMoic)} subtext="Recoup + commission" />
      </div>

      {/* Recoup progress */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-5">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <KTitleSpan text="Recoup Progress" fontSize={16} fontWeight={700} />
          <div className="flex gap-2">
            <Badge text={`Company Recoup: ${selectedDeal.companyRecoupLag} months`} bg={GREEN} color="white" />
            <Badge text={`Artist Recoup: ${selectedDeal.artistRecoupLag} months`} bg={TEXT_PRIMARY} color="white" />
          </div>
        </div>
        <ProgressBar label="COMPANY RECOUP" pctValue={selectedDeal.companyRecoupPct} color={GREEN} />
        <ProgressBar label="ARTIST RECOUP" pctValue={selectedDeal.artistRecoupPct} color={TEXT_PRIMARY} />
        <div className="flex justify-between flex-wrap gap-4 pt-2 border-t border-gray-100">
          <Milestone label="DEAL START" value={monthYear(selectedDeal.dealDate)} />
          <Milestone
            label="TODAY"
            value={new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
          />
          <Milestone label="COMPANY RECOUP" value={monthYear(selectedDeal.companyRecoupDate)} />
          <Milestone label="ARTIST RECOUP" value={monthYear(selectedDeal.artistRecoupDate)} />
          <Milestone label="CONTRACT END" value={monthYear(selectedDeal.contractEnd)} />
        </div>
      </div>

      {/* Monthly performance + deal detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {<MonthlyPerformanceChart selectedDeal={selectedDeal} />}

        <div className="lg:col-span-1">{<TrancheHistory selectedDeal={selectedDeal} />}</div>
      </div>

      {/* Monthly breakdown */}
      {<MonthlyBreakdownTable selectedDeal={selectedDeal} />}
    </div>
  )
}

export default AdvanceDealDetailPage
